import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { X, Loader2 } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { ScreenHead, ListCard, Skeletons, Empty, useClients, useProviders } from "./psPrimitives";
import { useClient } from "@/lib/clientContext";
import { ConfirmSheet } from "./ProvidersScreen";
import { tovSlug, cleanTovName, slotCount, providersOfClient, parseEstimate } from "@/lib/tov";

const IND = "#5856D6";
const RED = "#B3261E";

export function TovListScreen() {
  const { selectedClientId } = useClient();
  const { data: clients = [], isLoading } = useClients();
  const { data: providers = [] } = useProviders();
  const cid = String(selectedClientId || clients[0]?.client_id || "");

  if (isLoading) return <><ScreenHead eyebrow="Your Account · Optional" title="Visit types" /><Skeletons /></>;
  const client = clients.find((c) => String(c.client_id) === cid);

  return (
    <div className="fade-up" data-testid="tov-list-screen">
      <ScreenHead eyebrow="Your Account · Optional" title="Visit types"
        lede="The visit types for a client. revQ tags every visit — dictated, telemedicine, wound, uploaded or pasted — with the best match from this list, or Not set. Telemedicine booking uses this list too." />
      {!clients.length ? <Empty>Add a client first.</Empty> : (
        <>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="text-[11px] font-semibold text-[var(--gray2)]">Client</span>
            <select value={cid} disabled data-testid="tov-client-select"
              className="rounded-[9px] border border-black/[.14] bg-white px-2.5 py-1.5 text-[12.5px] text-[var(--ink)]">
              {clients.filter((c) => String(c.client_id) === cid).map((c) => <option key={c.client_id} value={String(c.client_id)}>{c.client_id}{c.name || c.description ? ` · ${c.name || c.description}` : ""}</option>)}
            </select>
          </div>
          {client ? <TovEditor key={cid} client={client} providers={providers} /> : null}
        </>
      )}
    </div>
  );
}

function TovEditor({ client, providers }) {
  const qc = useQueryClient();
  const cid = String(client.client_id);
  const [list, setList] = useState(Array.isArray(client.tov_list) ? client.tov_list : []);
  const [text, setText] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [rm, setRm] = useState(null);
  const [ren, setRen] = useState(null);
  const loaded = React.useRef(list);
  const rename = async (from, raw) => {
    const to = cleanTovName(raw);
    if (!to || to === from) { setRen(null); return; }
    if (list.some((n) => n !== from && n.toLowerCase() === to.toLowerCase())) { setMsg(`${to} is already in the list.`); return; }
    setSaving(true); setMsg("");
    try {
      await api.patch(`/api/edi/televisit/visit-types/${tovSlug(from)}?client_id=${encodeURIComponent(cid)}`, { name: to });
      const next = list.map((n) => (n === from ? to : n));
      setList(next); loaded.current = next; setRen(null);
      qc.invalidateQueries({ queryKey: ["clients"] }); qc.invalidateQueries({ queryKey: ["providers"] });
    } catch (e) { setMsg(describeError(e)); }
    setSaving(false);
  };

  // full-list PATCH, re-GET-and-compare guard (no version field)
  const persist = async (next) => {
    setSaving(true); setMsg("");
    try {
      const fresh = (await api.get("/api/edi/note-to-claim/clients")).data?.clients ?? [];
      const srv = fresh.find((c) => String(c.client_id) === cid)?.tov_list || [];
      if (JSON.stringify(srv) !== JSON.stringify(loaded.current)) {
        setList(srv); loaded.current = srv;
        setMsg("This list changed somewhere else — we reloaded the latest. Re-apply your edit.");
        setSaving(false); return false;
      }
      await api.patch(`/api/edi/note-to-claim/clients/${cid}`, { client_id: client.client_id, tov_list: next });
      setList(next); loaded.current = next;
      qc.invalidateQueries({ queryKey: ["clients"] });
      setSaving(false); return true;
    } catch (e) { setMsg(describeError(e)); setSaving(false); return false; }
  };

  const add = async () => {
    const v = cleanTovName(text);
    if (!v) return;
    if (list.some((n) => n.toLowerCase() === v.toLowerCase())) { setMsg(`${v} is already in the list.`); return; }
    if (await persist([...list, v])) setText("");
  };

  return (
    <div data-testid="tov-editor">
      <div className="flex items-stretch mb-2 rounded-[12px] border border-black/[.14] bg-white overflow-hidden focus-within:border-[#5856D6] transition">
        <input value={text} onChange={(e) => { setText(e.target.value); setMsg(""); }} disabled={saving}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid="tov-add-input" placeholder="Add a visit type — e.g. Follow-up Visit"
          className="flex-1 px-3 py-2.5 text-[13px] outline-none bg-transparent" />
        {saving ? <Loader2 size={14} className="animate-spin self-center mr-3 text-[var(--gray2)]" /> : null}
      </div>
      {msg ? <div className="mb-3 text-[11.5px] font-semibold" style={{ color: RED }} data-testid="tov-msg">{msg}</div> : null}

      {!list.length ? (
        <div className="rounded-[14px] border border-dashed border-[#D4D4DA] px-4 py-6 text-center text-[12.5px] text-[var(--gray2)] mt-3" data-testid="tov-empty">
          No visit types for this client yet. Visits are tagged Not set until you add some.
        </div>
      ) : (
        <div className="mt-3"><ListCard testid="tov-rows">
          {list.map((n, i) => {
            const k = slotCount(providers, cid, n);
            return (
              <div key={n} data-testid={`tov-row-${tovSlug(n)}`} className={`flex items-center gap-3 px-4 py-2.5 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
                <div className="flex-1 min-w-0">
                  {ren === n ? (
                    <>
                      <input autoFocus defaultValue={n} onFocus={(e) => e.target.select()} disabled={saving} data-testid={`tov-rename-input-${tovSlug(n)}`}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); rename(n, e.currentTarget.value); } else if (e.key === "Escape") { e.preventDefault(); setRen(null); setMsg(""); } }}
                        className="w-full rounded-[8px] border border-[#5856D6] bg-white px-2 py-1 text-[12.5px] font-semibold text-[var(--ink)] outline-none" />
                      <div className="text-[10.5px] text-[var(--gray2)] mt-1" data-testid="tov-rename-hint">Return to save · Esc to cancel{k ? " · provider templates move with it" : ""}</div>
                    </>
                  ) : <>
                    <div className="text-[12.5px] font-semibold text-[var(--ink)] truncate">{n}</div>
                    <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">{k ? `${k} provider template${k > 1 ? "s" : ""}` : "Uses each provider's Default template"}</div>
                  </>}
                </div>
                {ren === n ? null : <button type="button" onClick={() => { setRen(n); setMsg(""); }} data-testid={`tov-rename-${tovSlug(n)}`} className="text-[12px] font-semibold text-[#4A4AC2] hover:underline">Rename</button>}
                <button onClick={() => setRm(n)} aria-label={`Remove ${n}`} data-testid={`tov-remove-${tovSlug(n)}`}
                  className="text-[var(--gray3)] hover:text-[#B3261E] p-1 transition"><X size={14} /></button>
              </div>
            );
          })}
        </ListCard></div>
      )}

      <AutoBuild client={client} providers={providers} list={list} onAdd={(names) => persist([...list, ...names])} />

      {rm ? (() => {
        const k = slotCount(providers, cid, rm);
        const body = (k ? `${k} provider${k > 1 ? "s" : ""} ${k > 1 ? "have" : "has"} a ${rm} template. ` : "")
          + `Visits already tagged ${rm} keep the tag. New visits can't be tagged ${rm}`
          + (k ? `, and ${rm} notes fall back to each provider's Default.` : ".");
        return <ConfirmSheet title={`Remove ${rm}?`} body={body} confirmLabel="Remove" busy={saving}
          onCancel={() => setRm(null)} onConfirm={async () => { if (await persist(list.filter((x) => x !== rm))) setRm(null); }} />;
      })() : null}
    </div>
  );
}

function AutoBuild({ client, providers, list, onAdd }) {
  const [busy, setBusy] = useState(false);
  const [sug, setSug] = useState(null);
  const [pick, setPick] = useState([]);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/api/edi/note-to-claim/clients/${client.client_id}/tov/estimate`, {});
      const r = parseEstimate(data);
      if (!r.taxonomies.length) r.taxonomies = [...new Set(providersOfClient(providers, client.client_id)
        .flatMap((p) => (p.taxonomies || []).map((t) => [t.code, t.desc].filter(Boolean).join(" · "))))];
      setSug(r); setPick([]);
    } catch (e) {
      if (e?.response?.status === 400) setErr("Link a provider to this client first — suggestions come from their taxonomies.");
      else setErr(describeError(e));
    }
    setBusy(false);
  };

  const have = new Set(list.map((n) => n.toLowerCase()));
  const shown = (sug?.names || []).filter((n) => !have.has(n.toLowerCase()));
  const toggle = (n) => setPick((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  const addSel = async () => { if (await onAdd(pick)) { setSug(null); setPick([]); } };

  return (
    <div className="mt-6">
      {!sug ? (
        <button onClick={run} disabled={busy} data-testid="tov-autobuild-btn"
          className="inline-flex items-center gap-2.5 text-left px-3.5 py-2 rounded-[11px] border bg-white hover:bg-[rgba(88,86,214,.05)] transition"
          style={{ borderColor: "rgba(88,86,214,.45)" }}>
          {busy ? <Loader2 size={14} className="animate-spin" style={{ color: IND }} /> : <img src="/revQ_emblem_black.webp" alt="" aria-hidden="true" data-testid="tov-autobuild-emblem" style={{ height: 14, width: "auto", objectFit: "contain", opacity: 0.9 }} />}
          <span>
            <span className="block text-[12.5px] font-semibold" style={{ color: "#4A4AC2" }}>Auto-Build Visit Types</span>
            <span className="block text-[10.5px] text-[var(--gray2)]" data-testid="tov-autobuild-sub">based on your entered taxonomy</span>
          </span>
        </button>
      ) : (
        <div className="rounded-[14px] p-4" style={{ background: "rgba(88,86,214,.06)", border: "1px solid rgba(88,86,214,.22)" }} data-testid="tov-suggest-panel">
          <div className="text-[12.5px] font-semibold text-[var(--ink)]">Suggested from your providers' taxonomies</div>
          <div className="text-[11px] text-[var(--gray2)] mt-0.5 mb-3">
            {sug.taxonomies.length ? `From ${sug.taxonomies.join(", ")}. ` : ""}Nothing is saved until you add them.
          </div>
          {shown.length ? (
            <div className="flex flex-wrap gap-2 mb-3">
              {shown.map((n) => {
                const on = pick.includes(n);
                return (
                  <button key={n} onClick={() => toggle(n)} aria-pressed={on} data-testid={`tov-sugg-${tovSlug(n)}`}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white transition"
                    style={{ border: `1px solid ${on ? IND : "#D4D4DA"}`, color: on ? "#4A4AC2" : "var(--ink2)", boxShadow: on ? "0 0 0 3px rgba(88,86,214,.16)" : "none" }}>{n}</button>
                );
              })}
            </div>
          ) : <div className="text-[11.5px] text-[var(--gray2)] mb-3" data-testid="tov-sugg-none">Every suggestion is already in the list.</div>}
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setSug(null)} data-testid="tov-sugg-dismiss" className="text-[12px] font-semibold px-3 py-1.5 rounded-full text-[var(--ink2)] hover:bg-black/[.04]">Dismiss</button>
            <button onClick={addSel} disabled={!pick.length} data-testid="tov-sugg-add"
              className="text-[12px] font-bold px-3.5 py-1.5 rounded-full text-white disabled:opacity-40" style={{ background: "#141412" }}>Add {pick.length} selected</button>
          </div>
        </div>
      )}
      {err ? <div className="mt-2 text-[11.5px] font-semibold" style={{ color: RED }} data-testid="tov-autobuild-error">{err}</div> : null}
    </div>
  );
}
